import { Theme, css } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { SyntheticEvent } from 'react';

import { Radius, Spacing, Typography } from 'chatie-design-system';

import EFinderImage from '@components/common/EFinderImage';
import SwipeableListContainer from '@components/common/SwipableListContainer';

import {
  IContentGalleryContent,
  IContentGalleryWithHeader,
} from '@base/outlookModel';
import { useGlobalContext } from '@contexts/global';
import { addDuration, isBefore } from '@utils/date';

import Badge from '../common/Badge';
import ProfileWithMessage from './ProfileWithMessage';

const groupCurationCSS = (theme: Theme) => css`
  display: flex-col;

  .chat-room-container {
    padding: ${Spacing.small}px 26px !important;
  }
  .chat-room-header {
    margin-left: ${Spacing.small}px;
  }

  color: ${theme.var.gray900};
`;

const listContainerCSS = css`
  display: flex;
  margin-top: ${Spacing.xsmall}px;
  gap: ${Spacing.small}px;
  padding: 0 ${Spacing.medium}px;
  .situationListContainer {
  }
`;

const situationContainerCSS = css`
  display: flex;
  flex-direction: column;
  justify-content: initial;
  width: 100px !important;
  align-items: center;
  gap: ${Spacing.xhsmall}px;
`;
const situationInfoCSS = css`
  > p {
    padding: 0 ${Spacing.xxsmall}px;
    ${Typography.body12};
    line-height: 145%;
    display: -webkit-box;
    word-wrap: break-all;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const situationImgCSS = css`
  height: 100px;
  width: 100px;
  top: 0;
  position: relative;

  .new-badge {
    ${Typography.body11};
    display: flex;
    align-items: center;
    height: 16px;
    position: absolute;
    z-index: 1;
    top: ${Spacing.xxsmall}px;
    left: ${Spacing.xxsmall}px;
  }
`;

export interface ChatSituationGroupCurationnProps {
  data: IContentGalleryWithHeader;
  onClickSituation?: (
    e: SyntheticEvent,
    content: IContentGalleryContent
  ) => void;
  onClickProfile: (id: number) => void;
}

const ContentGalleryWithHeader = ({
  data,
  onClickProfile,
  onClickSituation,
}: ChatSituationGroupCurationnProps) => {
  const { t } = useTranslation();
  const { isCheming } = useGlobalContext();
  const { header, contents } = data;

  const isSituationType = data.content_object_type === 'situation';

  return (
    <div css={groupCurationCSS}>
      {isSituationType && (
        <ProfileWithMessage data={header} onClickProfile={onClickProfile} />
      )}
      <SwipeableListContainer className="situationListContainer">
        <div css={listContainerCSS}>
          {contents?.map((content) => {
            const isNewSituation =
              (content?.payload?.released_at &&
                isBefore(
                  new Date(),
                  addDuration(new Date(content?.payload?.released_at), {
                    days: 3,
                  })
                )) ||
              false;
            return (
              <div
                css={situationContainerCSS}
                key={content.id}
                onClick={(e) => {
                  if (isCheming) {
                    return onClickProfile && onClickProfile(header.id);
                  }

                  onClickSituation && onClickSituation(e, content);
                }}
              >
                <div css={situationImgCSS}>
                  {isNewSituation && (
                    <Badge
                      text={'NEW'}
                      className={'new-badge'}
                      backgroundColor={
                        isCheming
                          ? 'linear-gradient(180deg, #7F00FF, #8E2DE2)'
                          : 'linear-gradient(90deg, #04C8D0, #1590D5)'
                      }
                      radius={Radius.xsmall}
                      fontSize={10}
                    />
                  )}
                  <EFinderImage
                    image={content.image}
                    h={100}
                    radius={5}
                    layout={'responsive'}
                  />
                </div>
                <div css={situationInfoCSS}>
                  <p className="multiline">{t(content.title)}</p>
                </div>
              </div>
            );
          })}
        </div>
      </SwipeableListContainer>
    </div>
  );
};

export default ContentGalleryWithHeader;
